import React from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";

import Alert from "@material-ui/lab/Alert";

import ListaEditable from "../../components/ListaEditable";
import {
  detalleDeNivel,
  duracionTotal,
  nivelConDuracion,
  nivelesAgrupados,
} from "../../model/presentacion";

const listaPropType = PropTypes.shape({
  items: PropTypes.array.isRequired,
  borrador: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setBorrador: PropTypes.func.isRequired,
  agregar: PropTypes.func.isRequired,
  quitar: PropTypes.func.isRequired,
});

// Cuantos nombres se muestran antes de resumir el resto.
const NOMBRES_VISIBLES = 4;

/** "ANA, LUIS, PEDRO y 3 más" */
const quienes = (vistos) => {
  const nombres = vistos.map(({ nombre }) => nombre);
  if (nombres.length <= NOMBRES_VISIBLES) return nombres.join(", ");
  return `${nombres.slice(0, NOMBRES_VISIBLES).join(", ")} y ${nombres.length - NOMBRES_VISIBLES} más`;
};

/**
 * Por cada nivel elegido que algun estudiante ya tiene este periodo: si es
 * mas de la mitad del grupo no se puede pedir (como en AGIL); si son menos,
 * solo se avisa quienes.
 */
const AvisosNivelesVistos = ({ nivelesElegidos, nivelesVistos }) => {
  if (!nivelesVistos) return null;
  const { total, niveles: revisados } = nivelesVistos;
  return revisados
    .filter(({ vistos }) => vistos.length > 0)
    .map(({ id, vistos, bloquea }) => {
      const nombre = nivelesElegidos.find((n) => `${n.id_capacitacion}` === `${id}`)?.nombre;
      return (
        <Alert key={id} severity={bloquea ? "error" : "info"} style={{ marginTop: 8 }}>
          <strong>{nombre}:</strong> {vistos.length} de {total} estudiantes ya lo tienen
          este periodo ({quienes(vistos)}).
          {bloquea ? " Es más de la mitad del grupo: quítalo y elige otro." : ""}
        </Alert>
      );
    });
};

AvisosNivelesVistos.propTypes = {
  nivelesElegidos: PropTypes.array.isRequired,
  nivelesVistos: PropTypes.shape({ total: PropTypes.number, niveles: PropTypes.array }),
};

AvisosNivelesVistos.defaultProps = { nivelesVistos: null };

/**
 * Paso Libros / Niveles: que se necesita. Libro a tu clase pide libros o
 * tematicas en texto libre; ARIA, niveles de su catalogo. Cada tipo usa solo
 * su lista.
 *
 * @param {Object} libros, nivelesElegidos listas de useListaEditable.
 * @param {Array} niveles catalogo de niveles de ARIA.
 */
const PasoMaterial = ({
  esAria,
  libros,
  nivelesElegidos,
  niveles,
  cargandoNiveles,
  nivelesVistos,
}) => {
  if (!esAria) {
    return (
      <ListaEditable
        items={libros.items}
        onAgregar={libros.agregar}
        onQuitar={libros.quitar}
        textoVacio="Agregue al menos un libro o tematica"
      >
        <TextField
          value={libros.borrador}
          label="Tematica / Libro"
          fullWidth
          onChange={({ target }) => libros.setBorrador(target.value)}
        />
      </ListaEditable>
    );
  }

  const sinNiveles = !cargandoNiveles && niveles.length === 0;

  // Un nivel no se pide dos veces: el que ya esta en la lista se deja a la
  // vista pero apagado. El servidor tambien los rechaza, pero para entonces
  // ya se lleno toda la solicitud.
  const yaElegidos = nivelesElegidos.items.map(
    ({ id_capacitacion }) => `${id_capacitacion}`,
  );

  const total = duracionTotal(nivelesElegidos.items);

  // Lo elegido muestra debajo su nivel, duracion y descripcion. Sale del
  // catalogo, asi tambien al editar una solicitud ya guardada.
  const elegidosConDetalle = nivelesElegidos.items.map((elegido) => {
    const evento = niveles.find(({ id }) => `${id}` === `${elegido.id_capacitacion}`);
    return evento ? { ...elegido, detalle: detalleDeNivel(evento) } : elegido;
  });

  return (
    <div>
      <ListaEditable
        items={elegidosConDetalle}
        onAgregar={nivelesElegidos.agregar}
        onQuitar={nivelesElegidos.quitar}
        textoVacio="Agregue al menos un nivel"
      >
        <FormControl className="form-control" fullWidth error={sinNiveles}>
          <InputLabel>Nivel</InputLabel>
          <Select
            value={nivelesElegidos.borrador}
            // Los encabezados de nivel no son opciones: su clic llega sin valor.
            onChange={({ target }) =>
              target.value !== undefined && nivelesElegidos.setBorrador(target.value)
            }
            // Cerrado se ve solo el evento; abierto, agrupado por nivel y
            // con la descripcion de cada uno.
            renderValue={(id) => {
              const elegido = niveles.find((nivel) => `${nivel.id}` === `${id}`);
              return elegido ? nivelConDuracion(elegido) : "";
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 420 } } }}
          >
            {nivelesAgrupados(niveles).flatMap(({ etiqueta, eventos }) => [
              // Se queda arriba al bajar; con fondo propio, el de un nivel
              // tapa al anterior en vez de verse uno encima del otro.
              <ListSubheader
                key={`grupo-${etiqueta}`}
                style={{ pointerEvents: "none", backgroundColor: "#fff", fontWeight: 600 }}
              >
                {etiqueta}
              </ListSubheader>,
              ...eventos.map((nivel) => (
                <MenuItem
                  key={nivel.id}
                  value={nivel.id}
                  disabled={yaElegidos.includes(`${nivel.id}`)}
                  style={{ whiteSpace: "normal", maxWidth: 640 }}
                >
                  <ListItemText
                    primary={nivelConDuracion(nivel)}
                    secondary={nivel.descripcion || null}
                  />
                </MenuItem>
              )),
            ])}
          </Select>
          {sinNiveles && (
            <FormHelperText>No hay niveles de ARIA configurados</FormHelperText>
          )}
        </FormControl>
      </ListaEditable>

      <AvisosNivelesVistos
        nivelesElegidos={nivelesElegidos.items}
        nivelesVistos={nivelesVistos}
      />

      {/* La franja que pide el docente la escribe el a mano, asi que ver
          cuanto suman los niveles es lo que le dice si le alcanza. */}
      {total && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 8, textAlign: "right" }}
        >
          <strong>Tiempo total:</strong>{" "}
          {total}
        </Typography>
      )}
    </div>
  );
};

PasoMaterial.propTypes = {
  esAria: PropTypes.bool.isRequired,
  libros: listaPropType.isRequired,
  nivelesElegidos: listaPropType.isRequired,
  niveles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      nombre: PropTypes.string.isRequired,
      duracion: PropTypes.number,
    }),
  ).isRequired,
  cargandoNiveles: PropTypes.bool,
  /** Lo que responde revisarNivelesVistos: quienes ya tienen cada nivel. */
  nivelesVistos: PropTypes.shape({
    total: PropTypes.number,
    niveles: PropTypes.array,
  }),
};

PasoMaterial.defaultProps = { cargandoNiveles: false, nivelesVistos: null };

export default PasoMaterial;
